'use strict';

var CallsDevice = require('./util/CallsDevice');
var CmdDevice = require('./util/CmdDevice');
var log = require('../../../log/Log').channel('GroupSeriesTelnet');

function GroupSeriesTelnet(
    ipAddress,
    username,
    password,
    onConnectStatus,
    onControlStatus,
    config
) {
    this.sleeping = false;
    this.volumeMute = false;
    this.contentSource = null;
    this.ignoreNextVcbuttonStop = false;
    this.lastCameraPreset = null;
    this.cameraCaps = {};
    this.cameraInfo = {};
    this.dialDigits = '';
    this.dialTimeout = null;
    this.supportsEagleEyeProducer = false;
    this.hasEagleEyeProducer = false;

    this.initializeSteps = [
        {
            state: 'wakingSystem',
            action: function () {
                this.sendCommand('wake');
                return false;
            }.bind(this),
        },
        {
            state: 'gettingVersion',
            action: function () {
                this.query('$firmwareVersion');
                return true;
            }.bind(this),
        },
        {
            state: 'gettingCameraTracking',
            action: function () {
                if (this.supportsEagleEyeProducer) {
                    this.query('$cameraTracking');
                    return true;
                }

                return false;
            }.bind(this),
        },
        {
            state: 'gettingCameras',
            action: function () {
                this.query('$cameras');
                return true;
            }.bind(this),
        },
        {
            state: 'gettingSelectedCamera',
            action: function () {
                this.query('$cameraSource');
                return true;
            }.bind(this),
        },
        {
            state: 'finishInitialization',
            action: function () {
                this.finishInitialization();
                return true;
            }.bind(this),
        },
    ];

    CallsDevice.call(this);
    CmdDevice.call(this, ipAddress, username, password, onConnectStatus, onControlStatus, config);
}

GroupSeriesTelnet.prototype = Object.create(CmdDevice.prototype);
GroupSeriesTelnet.prototype.constructor = GroupSeriesTelnet;

Object.assign(GroupSeriesTelnet.prototype, CallsDevice.prototype);

GroupSeriesTelnet.prototype.name = 'GroupSeriesTelnet';
GroupSeriesTelnet.prototype.key = '-KD9ND5HJcdxHyUKUVYg';
GroupSeriesTelnet.prototype.negotiateTelnet = true;
GroupSeriesTelnet.prototype.pollIntervalMs = 2000;
GroupSeriesTelnet.prototype.cameraLimits = {
    x: { min: -50000, max: 50000 },
    y: { min: -50000, max: 50000 },
    z: { min: -50000, max: 50000 },
};

GroupSeriesTelnet.prototype.ports = [
   {
        port: 4999, // Global Cache Ethernet to RS-232 bridge
        verifySend: '\n\nexit\n\nwhoami\n',
        verifyConnection: /Hi, my name is|Welcome to Poly|Welcome* to ViewStation|login failed, retry/,
        verifyTimeoutMs: 5000,
        loginSkip: /Here is what I know about myself/,
        passwordPrompt: /Password:/,
        passwordPromptTimeoutMs: 1000,
        passwordDefault: null,
        passwordSend: function (value) {
            return value;
        },
        loginVerify: /Hi, my name is/,
        loginReject: /password failed/,
        loginVerifyTimeoutMs: 1000,
    },
    {
        port: 24, // direct telnet connection
        verifyConnection: /Hi, my name is|Welcome to Poly|login failed, retry/,
        verifyTimeoutMs: 5000,
        loginSkip: /Here is what I know about myself/,
        passwordPrompt: /Password:/,
        passwordPromptTimeoutMs: 1000,
        passwordDefault: null,
        passwordSend: function (value) {
            return value;
        },
        loginVerify: /Hi, my name is/,
        loginReject: /password failed/,
        loginVerifyTimeoutMs: 1000,
    },
];

GroupSeriesTelnet.prototype.simulationParams = {
    caps: {
        hasFirmmareVersion: true,
        hasRoomMute: true,
        hasRoomVolume: true,
        hasRoomVolumeMute: true,
        hasVideoInputs: true,
        hasVideoSwitcher: true,
        maxCalls: {
            pstn: 0,
            video: 1,
            voip: 0,
        },
        numCameras: 1,
        videoInputsAreContent: true,
    },
    callHasReceiveGain: function (ci, config, detected) {
        return false;
    },
    configNotRequired: {
        roomMute: true,
        roomVolume: true,
    },
    // detected: not supported on GroupSeries
    firmwareVersion: '6.1.0',
    // channelsAreEqual: not supported on GroupSeries
    // channelMatchesDetected: not supported on GroupSeries
    // getTelephonyType: not supported on GroupSeries
    // telephonyChannels: not supported on GroupSeries
};

GroupSeriesTelnet.prototype.commands = {};

GroupSeriesTelnet.prototype.commands.$callConnected = {};
GroupSeriesTelnet.prototype.commands.$callConnected.match = /(callinfo):(\d+):(.+?):(.+?):.+?:connected:.+?:(incoming|outgoing):.+|notification:(callstatus):(incoming|outgoing):(\d+):(.+?):(.+?):connected:.+?:.+?:.+/;
GroupSeriesTelnet.prototype.commands.$callConnected.process = function (match) {
    var plcmCallId, contactName, dialString, direction;

    if (match[1] === 'callinfo') {
        plcmCallId = match[2];
        contactName = match[3];
        dialString = match[4];
        direction = match[5];
    } else {
        // match[6] === 'callstatus'
        direction = match[7];
        plcmCallId = match[8];
        contactName = match[9];
        dialString = match[10];
    }

    var ci = this.getCallInterfaceByPlcmCallId(plcmCallId);

    if (ci.hasOwnProperty('callStatus') && ci.callStatus.hasOwnProperty('dialString')) {
        dialString = undefined;
    }

    if (direction === 'incoming') {
        this.updateCallerId(ci, dialString, contactName);
        this.answerCall(ci);
    } else {
        this.connectCall(ci, this.saveCallId, dialString, contactName);
        delete this.saveCallId;
    }
};

GroupSeriesTelnet.prototype.commands.$callDisconnected = {};
GroupSeriesTelnet.prototype.commands.$callDisconnected.match = /ended: call\[(\d+)\]/;
GroupSeriesTelnet.prototype.commands.$callDisconnected.process = function (match) {
    var ci = this.getCallInterfaceByPlcmCallId(match[1]);
    this.disconnectCall(ci);
};

GroupSeriesTelnet.prototype.commands.$callDisconnecting = {};
GroupSeriesTelnet.prototype.commands.$callDisconnecting.match = /(callinfo):(\d+):(.+?):(.+?):.+?:disconnecting:.+?:(incoming|outgoing):.+|notification:(callstatus):(incoming|outgoing):(\d+):(.+?):(.+?):disconnecting:.+?:.+?:.+/;
GroupSeriesTelnet.prototype.commands.$callDisconnecting.process = function (match) {
    var plcmCallId, contactName, dialString;

    if (match[1] === 'callinfo') {
        plcmCallId = match[2];
        contactName = match[3];
        dialString = match[4];
        //direction = match[5];
    } else {
        // match[6] === 'callstatus'
        //direction = match[7];
        plcmCallId = match[8];
        contactName = match[9];
        dialString = match[10];
    }

    var ci = this.getCallInterfaceByPlcmCallId(plcmCallId);

    if (ci.hasOwnProperty('callStatus') && ci.callStatus.callState === 'disconnected') return;

    if (ci.hasOwnProperty('callStatus') && ci.callStatus.hasOwnProperty('dialString')) {
        dialString = undefined;
    }

    this.endCall(ci, dialString, contactName);
};

// The $callEnded command is for handling the special case of an outgoing call
// that is terminated locally before the remote side answers. In this case, the
// GroupSeries does not return any callinfo or callstatus messages. It only
// sends a message of 'popupinfo: question: The call has ended.' We don't know
// which call ended, so when we see that message, we look at all the outgoing
// ringing calls and mark them as disconnected.
//
// This does create a bug if there are multiple outgoing ringing calls active,
// then all of them will be marked as disconnected when one of them is
// terminated locally. This case seems extremely rare, and it is better to have
// that behavior than not being able to detect when an outgoing ringing call is
// terminated locally.

GroupSeriesTelnet.prototype.commands.$callEnded = {};
GroupSeriesTelnet.prototype.commands.$callEnded.match = /popupinfo: question: The call has ended/;
GroupSeriesTelnet.prototype.commands.$callEnded.process = function (match) {
    for (var i = 0; i < this.callInterfaces.length; i++) {
        var ci = this.callInterfaces[i];

        if (
            ci.callType === 'video' &&
            ci.direction === 'outgoing' &&
            (ci.callState === 'ringing' || ci.callState === 'disconnecting')
        ) {
            this.disconnectCall(ci);
        }
    }
};

GroupSeriesTelnet.prototype.commands.$callRinging = {};
GroupSeriesTelnet.prototype.commands.$callRinging.match = /(callinfo):(\d+):(.*?):(.*?):.*?:(opened|ringing|connecting):.*?:(incoming|outgoing):.+|notification:(callstatus):(incoming|outgoing):(\d+):(.*?):(.*?):(opened|ringing):.*?:.*?:.+/;
GroupSeriesTelnet.prototype.commands.$callRinging.process = function (match) {
    var plcmCallId, contactName, dialString, direction;

    if (match[1] === 'callinfo') {
        plcmCallId = match[2];
        contactName = match[3];
        dialString = match[4];
        //callState = match[5];
        direction = match[6];
    } else {
        // match[7] = 'callstatus'
        direction = match[8];
        plcmCallId = match[9];
        contactName = match[10];
        dialString = match[11];
        //callState = match[12];
    }

    var ci = this.getCallInterfaceByPlcmCallId(plcmCallId);

    if (direction === 'incoming') {
        this.setIncomingCall(ci, dialString, contactName);
    } else {
        if (
            ci.hasOwnProperty('callStatus') &&
            ci.callStatus.direction === 'outgoing' &&
            ci.callStatus.hasOwnProperty('dialString')
        ) {
            dialString = undefined;
        }

        this.setOutgoingCall(ci, dialString, contactName, undefined, this.saveCallId);
        delete this.saveCallId;
    }
};

GroupSeriesTelnet.prototype.commands.$cameraSource = {};
GroupSeriesTelnet.prototype.commands.$cameraSource.activeStates = ['initializing'];
GroupSeriesTelnet.prototype.commands.$cameraSource.activeInitializeStates = [
    'gettingSelectedCamera',
];
GroupSeriesTelnet.prototype.commands.$cameraSource.get = function () {
    this.sendCommand('camera near source');
};
GroupSeriesTelnet.prototype.commands.$cameraSource.match = /^camera near source (\d+)/;
GroupSeriesTelnet.prototype.commands.$cameraSource.process = function (match) {
    var index = parseInt(match[1], 10);

    for (var cameraId in this.cameraInfo) {
        if (!this.cameraInfo.hasOwnProperty(cameraId)) continue;
        if (this.cameraInfo[cameraId].index === index) {
            this.selectedCamera = cameraId;
            break;
        }
    }

    this.doNextInitializeStep();
};

GroupSeriesTelnet.prototype.commands.$cameraTracking = {};
GroupSeriesTelnet.prototype.commands.$cameraTracking.activeStates = ['initializing', 'controlling'];
GroupSeriesTelnet.prototype.commands.$cameraTracking.activeInitializeStates = [
    'gettingCameraTracking',
];
GroupSeriesTelnet.prototype.commands.$cameraTracking.get = function () {
    this.sendCommand('camera near tracking get');
};
GroupSeriesTelnet.prototype.commands.$cameraTracking.match = /(error:)|camera near tracking (.+)/;
GroupSeriesTelnet.prototype.commands.$cameraTracking.process = function (match) {
    if (typeof match[1] !== 'undefined') {
        this.hasEagleEyeProducer = false;
        this.cameraTracking = false;
    } else {
        this.hasEagleEyeProducer = true;
        this.cameraTracking = match[2] !== 'off';
    }

    if (this.fsm.current === 'initializing') this.doNextInitializeStep();
};

GroupSeriesTelnet.prototype.commands.$cameras = {};
GroupSeriesTelnet.prototype.commands.$cameras.activeStates = ['initializing'];
GroupSeriesTelnet.prototype.commands.$cameras.activeInitializeStates = ['gettingCameras'];
GroupSeriesTelnet.prototype.commands.$cameras.get = function () {
    this.sendCommand('systemsetting get cameracontent');
    this.sendCommand('systemsetting get cameracontent1');
    this.sendCommand('systemsetting get cameracontent2');
    this.sendCommand('systemsetting get cameracontent3');
};
GroupSeriesTelnet.prototype.commands.$cameras.match = /systemsetting cameracontent(\d*) (.+)|error: input (\d+) is not available|(error:\s+command)/;
GroupSeriesTelnet.prototype.commands.$cameras.process = function (match) {
    var index;

    if (typeof match[1] === 'undefined') {
        index = parseInt(match[3], 10);
    } else {
        index = match[1] === '' ? 1 : parseInt(match[1], 10) + 1;

        if (match[2] === 'People') {
            var cameraId = this.generateId();
            if (this.firstCamera === null) this.firstCamera = cameraId;
            this.cameraCaps[cameraId] = {
                name: 'Camera ' + index,
                hasCameraTracking: index === 1 && this.hasEagleEyeProducer,
                hasCameraTrackingUi:
                    index === 1 &&
                    this.hasEagleEyeProducer &&
                    this.getNode(this.config, 'cameraTrackingUiEnabled', true),
                hasPan: true,
                hasTilt: true,
                hasZoom: true,
                location: 'near',
            };
            this.cameraInfo[cameraId] = {
                index: index,
            };
        }
    }

    // Tested four cameras or ran out of cameras (G7500)
    if (index === 4 || match[4] !== 'undefined') this.doNextInitializeStep();
};

// Typically we get the dial string and contact name from the callinfo and
// callstatus messages. However, there are cases where those messages don't
// contain the dial string or contact name, but the cs message might.

GroupSeriesTelnet.prototype.commands.$dialString = {};
GroupSeriesTelnet.prototype.commands.$dialString.match = /cs:\s*call\[(\d+)\]\s+chan\[\d+\]\s+dialstr\[(.+?)\]\s+state\[.+\]/;
GroupSeriesTelnet.prototype.commands.$dialString.process = function (match) {
    var plcmCallId = match[1];
    var dialString = match[2];
    var ci = this.getCallInterfaceByPlcmCallId(plcmCallId);

    if (
        !ci.hasOwnProperty('callStatus') ||
        !ci.callStatus.hasOwnProperty('dialString') ||
        ci.callStatus.dialString === ''
    ) {
        this.updateCallerId(ci, dialString);
    }
};

GroupSeriesTelnet.prototype.commands.$firmwareVersion = {};
GroupSeriesTelnet.prototype.commands.$firmwareVersion.activeStates = ['initializing'];
GroupSeriesTelnet.prototype.commands.$firmwareVersion.activeInitializeStates = ['gettingVersion'];
GroupSeriesTelnet.prototype.commands.$firmwareVersion.get = function () {
    this.sendCommand('version');
};
GroupSeriesTelnet.prototype.commands.$firmwareVersion.match = /^version "?([^"]+)/;
GroupSeriesTelnet.prototype.commands.$firmwareVersion.process = function (match) {
    this.firmwareVersion = match[1];

    var codeRegExp = /(\d+).(\d+).(\d+)/;
    var codeMatch = codeRegExp.exec(this.firmwareVersion);

    if (codeMatch) {
        var major = parseInt(codeMatch[1], 10);
        var minor = parseInt(codeMatch[2], 10);
        var patch = parseInt(codeMatch[3], 10);
        var code = 1000000 * major + 1000 * minor + patch;

        if (code >= 6001000) {
            this.supportsEagleEyeProducer = true;
        }
    }

    this.doNextInitializeStep();
};

// Sometimes the dial string is not present in the callstatus messages for
// outgoing calls. If we detect that, then we schedule a query for the
// callinfo for that call.

GroupSeriesTelnet.prototype.commands.$outgoingCall = {};
GroupSeriesTelnet.prototype.commands.$outgoingCall.match = /notification:callstatus:outgoing:(\d+):(.*):(.*):opened:/;
GroupSeriesTelnet.prototype.commands.$outgoingCall.process = function (match) {
    var plcmCallId = match[1];
    var dialString = match[2];
    //var contactName = match[3];

    if (dialString === '') {
        log.warning('call status with no dial string, scheduling query');
        window.setTimeout(
            function () {
                if (this.fsm.current !== 'controlling') return;
                this.sendCommand('callinfo callid ' + plcmCallId);
            }.bind(this),
            500
        );
    }
};

GroupSeriesTelnet.prototype.commands.$nocontent = {};
GroupSeriesTelnet.prototype.commands.$nocontent.match = /^popupinfo: question: Please check your content cable/;
GroupSeriesTelnet.prototype.commands.$nocontent.process = function (match) {
    if (this.name === 'HDX') {
        this.suspend(12000);

        if (this.contentSource !== this.noContentId && this.contentSource !== null) {
            this.broadcastStatus('contentState', {
                id: this.contentSource,
                state: null,
            });
        }

        this.contentSource = this.noContentId;
        this.broadcastStatus('contentSource', this.contentSource);
    }
};

GroupSeriesTelnet.prototype.commands.$sleeping = {};
GroupSeriesTelnet.prototype.commands.$sleeping.match = /listen (going to sleep|waking up)/;
GroupSeriesTelnet.prototype.commands.$sleeping.process = function (match) {
    var newSleeping = match[1] === 'going to sleep' ? true : false;

    if (newSleeping !== this.sleeping) {
        this.sleeping = newSleeping;

        if (!this.sleeping) {
            this.setBusy(true, 0);
            this.setBusy(false, 18000);
        }

        this.broadcastStatus('power', !this.sleeping);
    }
};

GroupSeriesTelnet.prototype.commands.answer = {};
GroupSeriesTelnet.prototype.commands.answer.set = function (callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error('answer: invalid callId ' + callId);
        return;
    }

    this.answerCall(ci);
    this.sendCommand('answer video');
};

GroupSeriesTelnet.prototype.commands.busy = {};
GroupSeriesTelnet.prototype.commands.busy.get = function () {
    this.broadcastStatus('busy', this.busy);
};

GroupSeriesTelnet.prototype.commands.call = {};
GroupSeriesTelnet.prototype.commands.call.set = function (value) {
    var ci = this.getAvailableCallInterface(value.callType);

    if (ci === null) {
        log.error('call: no ' + value.callType + ' resources available');
        return;
    }

    // Don't call placeCall because we don't know the plcmCallId yet. Send the
    // command to place the call, and let the status messages trigger the call
    // to connectCall.

    this.saveCallId = value.callId; // save for when we finally call connectCall

    this.sendCommand('dial manual auto "' + value.dialString + '"');
};

GroupSeriesTelnet.prototype.commands.callStatus = {};
GroupSeriesTelnet.prototype.commands.callStatus.get = function () {
    this.sendCallStatusMessages();
};

GroupSeriesTelnet.prototype.commands.cameraMove = {};
GroupSeriesTelnet.prototype.commands.cameraMove.set = function (value) {
    if (!value.hasOwnProperty('action')) return;

    if (!this.cameraInfo.hasOwnProperty(value.cameraId)) {
        log.error(this + ': cameraMove.set: invalid cameraId ' + value.cameraId);
        return;
    }

    if (value.action === 'start') {
        if (this.cameraMoveDirection !== null) {
            log.error(this + ': cameraMove.set: received start while moving');
            this.sendCommand('camera near move stop');
        }

        this.cameraMoveDirection = value.direction;
        var direction = value.direction;

        if (direction === 'in') {
            direction = 'zoom+';
        } else if (direction === 'out') {
            direction = 'zoom-';
        }

        this.sendCommand('camera near move ' + direction);
    } else {
        // value.action === 'stop'
        this.cameraMoveDirection = null;
        this.sendCommand('camera near move stop');
    }
};

GroupSeriesTelnet.prototype.commands.cameraPreset = {};
GroupSeriesTelnet.prototype.commands.cameraPreset.get = function () {
    this.broadcastStatus('cameraPreset', this.lastCameraPreset);
};
GroupSeriesTelnet.prototype.commands.cameraPreset.set = function (value) {
    if (
        !this.config.hasOwnProperty('cameraPresets') ||
        !this.config.cameraPresets.hasOwnProperty(value)
    ) {
        log.error(this + '; cameraPreset.set: invalid presetId ' + value);
        return;
    }

    this.lastCameraPreset = value;
    this.broadcastStatus('cameraPreset', this.lastCameraPreset);
    this.sendCommand('preset near go ' + this.config.cameraPresets[value].presetIndex);
};
GroupSeriesTelnet.prototype.commands.cameraPreset.match = /preset near go (\d+)/;
GroupSeriesTelnet.prototype.commands.cameraPreset.process = function (match) {};

GroupSeriesTelnet.prototype.commands.cameraSelected = {};
GroupSeriesTelnet.prototype.commands.cameraSelected.get = function () {
    for (var cameraId in this.cameraInfo) {
        if (!this.cameraInfo.hasOwnProperty(cameraId)) continue;
        this.broadcastStatus('cameraSelected', {
            cameraId: cameraId,
            value: cameraId === this.selectedCamera,
        });
    }
};
GroupSeriesTelnet.prototype.commands.cameraSelected.set = function (cameraId) {
    if (!this.cameraInfo.hasOwnProperty(cameraId)) {
        log.error(this + ': cameraSelected.set: invalid cameraId ' + cameraId);
        return;
    }

    if (this.selectedCamera !== null && this.selectedCamera !== cameraId) {
        this.broadcastStatus('cameraSelected', {
            cameraId: this.selectedCamera,
            value: false,
        });
    }

    this.selectedCamera = cameraId;

    this.broadcastStatus('cameraSelected', {
        cameraId: cameraId,
        value: true,
    });

    this.sendCommand('camera near ' + this.cameraInfo[cameraId].index);
    this.sendCommand('camera near getposition');
};
GroupSeriesTelnet.prototype.commands.cameraSelected.match = /camera near (\d+)|notification:vidsourcechange:near:(\d+):[^:]+:.+/;
GroupSeriesTelnet.prototype.commands.cameraSelected.process = function (match) {
    var index = parseInt(typeof match[1] === 'undefined' ? match[2] : match[1], 10);

    for (var cameraId in this.cameraInfo) {
        if (!this.cameraInfo.hasOwnProperty(cameraId)) continue;
        if (this.cameraInfo[cameraId].index === index) {
            if (this.selectedCamera !== cameraId) {
                if (this.selectedCamera !== null) {
                    this.broadcastStatus('cameraSelected', {
                        cameraId: this.selectedCamera,
                        value: false,
                    });
                }

                this.selectedCamera = cameraId;

                this.broadcastStatus('cameraSelected', {
                    cameraId: this.selectedCamera,
                    value: true,
                });
            }

            break;
        }
    }
};

GroupSeriesTelnet.prototype.commands.cameraTracking = {};
GroupSeriesTelnet.prototype.commands.cameraTracking.get = function () {
    for (var cameraId in this.cameraInfo) {
        if (!this.cameraInfo.hasOwnProperty(cameraId)) continue;

        this.broadcastStatus('cameraTracking', {
            cameraId: cameraId,
            value: this.cameraTracking && cameraId === this.firstCamera,
        });
    }
};
GroupSeriesTelnet.prototype.commands.cameraTracking.set = function (value) {
    if (this.selectedCamera !== value.cameraId) {
        this.set('cameraSelected', value.cameraId);
    }

    this.cameraTracking = value.value;
    this.sendCommand('camera near tracking ' + (value.value ? 'on' : 'off'));
    this.broadcastStatus('cameraTracking', {
        cameraId: value.cameraId,
        value: this.cameraTracking,
    });
};
GroupSeriesTelnet.prototype.commands.cameraTracking.match = /(error:)|camera near tracking (.+)/;
GroupSeriesTelnet.prototype.commands.cameraTracking.process = function (match) {
    var newValue = true;

    if (typeof match[1] !== 'undefined' || match[2] === 'off') {
        newValue = false;
    }

    if (newValue !== this.cameraTracking) {
        this.cameraTracking = newValue;
        this.broadcastStatus('cameraTracking', {
            cameraId: this.selectedCamera,
            value: this.cameraTracking,
        });
    }
};

GroupSeriesTelnet.prototype.commands.dial = {};
GroupSeriesTelnet.prototype.commands.dial.set = function (value) {
    var ci = this.getCallInterfaceByCallId(value.callId);

    if (ci === null) {
        log.error('dial: invalid callId ' + value.callId);
        return;
    }

    this.dialCall(ci, value.dialString);
    this.dialDigits += value.dialString;

    if (this.dialTimeout === null) this.onDialTimeout();
};

GroupSeriesTelnet.prototype.commands.firmwareVersion = {};
GroupSeriesTelnet.prototype.commands.firmwareVersion.get = function () {
    this.broadcastStatus('firmwareVersion', this.firmwareVersion);
};

GroupSeriesTelnet.prototype.commands.hangup = {};
GroupSeriesTelnet.prototype.commands.hangup.set = function (callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error('hangup: invalid callId ' + callId);
        return;
    }

    if (
        ci.hasOwnProperty('callStatus') &&
        ci.callStatus.direction === 'outgoing' &&
        ci.callStatus.callState === 'ringing'
    ) {
        // When hanging up an outgoing ringing call, the GroupSeries does not
        // send any status messages back. So, we go straight to disconnectCall
        // for this case.

        this.disconnectCall(ci);
    } else {
        this.endCall(ci);
    }

    if (ci.hasOwnProperty('plcmCallId')) {
        this.sendCommand('hangup video ' + ci.plcmCallId);
    } else {
        this.sendCommand('hangup video');
    }
};

GroupSeriesTelnet.prototype.commands.mute = {};
GroupSeriesTelnet.prototype.commands.mute.get = function () {
    this.sendCommand('mute near get');
};
GroupSeriesTelnet.prototype.commands.mute.set = function (value) {
    this.sendCommand('mute near ' + (value ? 'on' : 'off'));
};
GroupSeriesTelnet.prototype.commands.mute.match = /^mute near (on|off)/;
GroupSeriesTelnet.prototype.commands.mute.process = function (match) {
    this.broadcastStatus('mute', match[1] === 'on');
};

GroupSeriesTelnet.prototype.commands.ping = {};
GroupSeriesTelnet.prototype.commands.ping.activeStates = ['controlling', 'controlFailed'];
GroupSeriesTelnet.prototype.commands.ping.get = function () {
    this.sendCommand('echo ping');
};
GroupSeriesTelnet.prototype.commands.ping.match = /^ping/;

GroupSeriesTelnet.prototype.commands.reject = {};
GroupSeriesTelnet.prototype.commands.reject.set = function (callId) {
    var ci = this.getCallInterfaceByCallId(callId);

    if (ci === null) {
        log.error('reject: invalid callId ' + callId);
        return;
    }

    // Don't call endCall. Let the status messages trigger it instead.

    if (ci.hasOwnProperty('plcmCallId')) {
        this.sendCommand('hangup video ' + ci.plcmCallId);
    } else {
        this.sendCommand('hangup video');
    }
};

GroupSeriesTelnet.prototype.commands.power = {};
GroupSeriesTelnet.prototype.commands.power.get = function () {
    this.broadcastStatus('power', !this.sleeping);
};
GroupSeriesTelnet.prototype.commands.power.set = function (value) {
    if (value && value === this.sleeping) {
        this.setBusy(true, 0);
        this.setBusy(false, 18000);
    }

    this.sleeping = !value;
    this.sendCommand(this.sleeping ? 'sleep' : 'wake');
    this.broadcastStatus('power', !this.sleeping);
};

GroupSeriesTelnet.prototype.commands.contentState = {};
GroupSeriesTelnet.prototype.commands.contentState.get = function () {
    for (var id in this.config.videoInputs) {
        if (!this.config.videoInputs.hasOwnProperty(id)) continue;
        if (id === this.noContentId) continue;
        this.broadcastStatus('contentState', {
            id: id,
            state: id === this.contentSource ? 'sending' : null,
        });
    }
};

GroupSeriesTelnet.prototype.commands.contentSource = {};
GroupSeriesTelnet.prototype.commands.contentSource.get = function () {
    if (this.contentSource === null) {
        this.sendCommand('vcbutton source get');
    } else {
        this.broadcastStatus('contentSource', this.contentSource);
    }
};
GroupSeriesTelnet.prototype.commands.contentSource.set = function (srcId) {
    if (this.getNode(this, ['config', 'videoInputs', srcId]) === null) {
        log.error(
            'contentSource: set: srcId ' +
                srcId +
                ' not present in videoInputs for config = ' +
                JSON.stringify(this.config.videoInputs, null, 4)
        );
        return;
    }

    var changed = this.contentSource !== srcId;

    if (this.contentSource !== null && this.contentSource !== this.noContentId && changed) {
        this.broadcastStatus('contentState', {
            id: this.contentSource,
            state: null,
        });
    }

    this.contentSource = srcId;
    this.broadcastStatus('contentSource', this.contentSource);

    if (this.contentSource === this.noContentId) {
        this.sendCommand('vcbutton stop');
    } else {
        if (changed) {
            this.broadcastStatus('contentState', {
                id: this.contentSource,
                state: 'sending',
            });
        }

        this.ignoreNextVcbuttonStop = true;
        this.sendCommand('vcbutton stop');
        this.sendCommand('vcbutton play ' + this.config.videoInputs[this.contentSource].code);
        // Send a get command since HDX doesn't always send back a stop if
        // there's no input connected.
        this.sendCommand('vcbutton source get');
    }
};
GroupSeriesTelnet.prototype.commands.contentSource.match = /vcbutton (stop)|play (\d+)|source .*(none)|source \D*(\d+)/;
GroupSeriesTelnet.prototype.commands.contentSource.process = function (match) {
    var contentSourceCode = null;

    if (match[1] === 'stop') {
        if (this.ignoreNextVcbuttonStop) {
            this.ignoreNextVcbuttonStop = false;
            return;
        }
    } else if (typeof match[2] !== 'undefined') {
        contentSourceCode = match[2];
    } else if (match[3] === 'none') {
        contentSourceCode = null;
    } else if (typeof match[4] !== 'undefined') {
        contentSourceCode = match[4];
    }

    var foundId = null;

    if (contentSourceCode === null) {
        foundId = this.noContentId;
    } else {
        for (var srcId in this.config.videoInputs) {
            if (!this.config.videoInputs.hasOwnProperty(srcId)) continue;

            if (contentSourceCode === this.config.videoInputs[srcId].code) {
                foundId = srcId;
                break;
            }
        }

        if (foundId === null && this.contentSource !== null) {
            log.warning('unmatched content source code: ' + contentSourceCode);
        }
    }

    if (foundId !== this.contentSource) {
        if (this.contentSource !== null && this.contentSource !== this.noContentId) {
            this.broadcastStatus('contentState', {
                id: this.contentSource,
                state: null,
            });
        }

        this.contentSource = foundId; // null is okay
        this.broadcastStatus('contentSource', this.contentSource);

        if (this.contentSource !== null && this.contentSource !== this.noContentId) {
            this.broadcastStatus('contentState', {
                id: this.contentSource,
                state: 'sending',
            });
        }
    }
};

GroupSeriesTelnet.prototype.commands.stopContent = {};
GroupSeriesTelnet.prototype.commands.stopContent.set = function (srcId) {
    if (srcId === this.noContentId) return;
    this.set('contentSource', this.noContentId);
};

GroupSeriesTelnet.prototype.commands.volume = {};
GroupSeriesTelnet.prototype.commands.volume.get = function () {
    this.sendCommand('volume get');
};
GroupSeriesTelnet.prototype.commands.volume.set = function (value) {
    this.sendCommand('volume set ' + value);
};
GroupSeriesTelnet.prototype.commands.volume.match = /^volume (\d+)/;
GroupSeriesTelnet.prototype.commands.volume.process = function (match) {
    var newVolume = Math.round(parseInt(match[1]));
    var newVolumeMute = newVolume <= 0;
    this.broadcastStatus('volume', newVolume);

    if (newVolumeMute !== this.volumeMute) {
        this.volumeMute = newVolumeMute;
        this.broadcastStatus('volumeMute', this.volumeMute);
    }
};

GroupSeriesTelnet.prototype.commands.volumeMute = {};
GroupSeriesTelnet.prototype.commands.volumeMute.get = function () {
    this.broadcastStatus('volumeMute', this.volumeMute);
};

GroupSeriesTelnet.prototype.getCallInterfaceByPlcmCallId = function (plcmCallId, create) {
    var ci;

    if (typeof create === 'undefined') create = true;

    for (var i = 0; i < this.callInterfaces.length; i++) {
        ci = this.callInterfaces[i];
        if (ci.plcmCallId === plcmCallId) {
            return ci;
        }
    }

    ci = this.getAvailableCallInterface('video');

    if (ci === null && create) {
        this.callInterfaces.push({
            configId: 'a' + this.callInterfaces.length,
            name: 'video' + this.callInterfaces.length,
            callType: 'video',
        });

        ci = this.callInterfaces[this.callInterfaces.length - 1];
        log.error('added call interface, callInterfaces.length = ' + this.callInterfaces.length);
        this.computeCaps();
    }

    if (ci !== null) ci.plcmCallId = plcmCallId;

    return ci;
};

GroupSeriesTelnet.prototype.onComputeCaps = function () {
    var caps = this.getCapsTemplate();
    caps.hasBusy = true;
    caps.hasFirmwareVersion = true;
    caps.hasRoomMute = true;
    caps.hasPowerControl = true;
    caps.hasRoomVolume = true;
    caps.hasRoomVolumeMute = true;
    caps.hasZoomConnector = this.config.zoomConnectorEnabled === true;
    caps.videoInputsAreContent = true;
    caps.hasVideoSwitcher = true;
    this.computeCapsMaxCalls(caps);
    this.computeVideoInputsCaps(caps);
    if (!caps.hasVideoInputs) caps.hasVideoSwitcher = false;
    caps.cameras = JSON.parse(JSON.stringify(this.cameraCaps));
    this.computeCameraPresetsCaps(caps);

    return caps;
};

GroupSeriesTelnet.prototype.onConnect = function () {
    this.resetCallInterfaces();
    this.finishConnecting();
};

GroupSeriesTelnet.prototype.onControl = function () {
    //this.sendCommand('all register');
    //this.sendCommand('audiotransmitlevel register');
    this.sendCommand('callstate register'); // included in 'all register'
    //this.sendCommand('camera register'); // deprecated - use notify vidsourcechanges
    //this.sendCommand('linestate register'); // included in 'all register'
    this.sendCommand('mute register'); // included in 'all register'
    this.sendCommand('popupinfo register'); // included in 'all register'
    //this.sendCommand('preset register'); // included in 'all register'
    //this.sendCommand('screen register'); // included in 'all register'
    this.sendCommand('sleep register');
    this.sendCommand('vcbutton register'); // included in 'all register'
    this.sendCommand('volume register'); // included in 'all register'
    //this.sendCommand('notify calendarmeetings');
    this.sendCommand('notify callstatus');
    //this.sendCommand('notify linestatus');
    //this.sendCommand('notify mutestatus');
    //this.sendCommand('notify screenchanges');
    //this.sendCommand('notify sysstatus');
    //this.sendCommand('notify sysalerts');
    this.sendCommand('notify vidsourcechanges');

    this.sendCommand('callinfo all');

    this.finishControlling();
};

// If you send DTMF digits too quickly, the Group Series will not play some of
// them. Slowing down to a rate of ~350 ms seems to resolve the problem.

GroupSeriesTelnet.prototype.onDialTimeout = function () {
    this.dialTimeout = null;

    if (this.dialDigits.length === 0) return;

    this.sendCommand('gendial ' + this.dialDigits[0]);
    this.dialDigits = this.dialDigits.slice(1);
    this.dialTimeout = window.setTimeout(this.onDialTimeout.bind(this), 400);
};

GroupSeriesTelnet.prototype.onInitialize = function () {
    this.busy = false;
    this.cameraCaps = {};
    this.cameraInfo = {};
    this.firstCamera = null;
    this.selectedCamera = null;
    this.lastCameraPreset = null;
    this.sleeping = false;
    this.supportsEagleEyeProducer = false;
    this.hasEagleEyeProducer = false;
    this.cameraMoveDirection = null;
    this.cameraTracking = false;
    this.pollCycle = 0;
    this.startInitializer();
};

GroupSeriesTelnet.prototype.onPoll = function () {
    if (this.sleeping) {
        this.pollCycle = 0;
        return;
    }

    switch (this.pollCycle) {
        case 0:
            this.sendCommand('vcbutton source get');
            break;
        case 1:
            this.sendCommand('camera near tracking get');
            break;
        default:
            log.error(this + ': invalid pollCycle = ' + this.pollCycle);
            break;
    }

    this.pollCycle++;

    if (this.supportsEagleEyeProducer && this.hasEagleEyeProducer) {
        if (this.pollCycle >= 2) this.pollCycle = 0;
    } else {
        if (this.pollCycle >= 1) this.pollCycle = 0;
    }
};

GroupSeriesTelnet.prototype.onUpdateConfig = function () {
    this.callInterfaces = [
        {
            configId: 'a0',
            name: 'video0',
            callType: 'video',
        },
    ];

    // Don't call the CallsDevice onUpdateConfig -- we are manually defining our
    // own callInterfaces.
    // CallsDevice.prototype.onUpdateConfig.call(this);
};

module.exports = GroupSeriesTelnet;
